import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { getSupabaseClient, verifyActivePlatformAdmin } from "@veorest/api-client";
import { AdminAuthLayout } from "../components/auth/AdminAuthLayout";
import { AuthField, AuthSubmit } from "../components/auth/AuthField";
import { ROUTES } from "../routes";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const supabase = getSupabaseClient();
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError.message);
        return;
      }

      const userId = authData.user?.id;
      if (!userId) {
        await supabase.auth.signOut();
        setError("Authentication failed.");
        return;
      }

      const { authorized } = await verifyActivePlatformAdmin(userId);
      if (!authorized) {
        await supabase.auth.signOut();
        setError("You are not authorized to access the platform admin console.");
        return;
      }

      navigate(ROUTES.DASHBOARD);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminAuthLayout
      title="Welcome back"
      subtitle="Sign in with your OELOX platform admin credentials. Accounts are provisioned manually."
      footerPrompt="Forgot your password?"
      footerLinkLabel="Reset it"
      footerLinkTo={ROUTES.FORGOT_PASSWORD}
    >
      {error ? (
        <div className="pill pill-danger mb-4 w-full justify-center text-center">{error}</div>
      ) : null}

      <form onSubmit={handleSubmit} className="auth-form space-y-4">
        <AuthField
          id="admin-email"
          label="Email"
          type="email"
          icon={Mail}
          value={email}
          onChange={setEmail}
          placeholder="admin@oelox.com"
          autoComplete="username"
          required
        />
        <AuthField
          id="admin-password"
          label="Password"
          type="password"
          icon={Lock}
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
          autoComplete="current-password"
          required
          minLength={6}
        />
        <AuthSubmit loading={isSubmitting}>
          {isSubmitting ? "Signing in…" : "Sign in to console"}
        </AuthSubmit>
      </form>
    </AdminAuthLayout>
  );
}
